import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, History, Loader2, Sparkles, ShieldCheck } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { API } from '../../App';
import { isIOSNative } from '../../utils/nativeBridge';

const fmtMonth = (iso) => {
  try { return new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(new Date(iso)); } catch { return iso; }
};

// Sep 2026 · Live-editable backlog choice. Every backlog process (statement
// upload, Mono, Plaid) reads the CURRENT value at run time, so switching here
// changes how the NEXT account/statement is handled - past ones are untouched.
export const BacklogChoiceSection = () => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/billing/backlog-choice`);
      setInfo(data);
    } catch { setInfo(null); }
  };
  useEffect(() => { load(); }, []);

  const choose = async (choice) => {
    if (!info || choice === info.choice || saving) return;
    setSaving(choice);
    try {
      await axios.post(`${API}/billing/backlog-choice`, { choice });
      setInfo((prev) => ({ ...prev, choice }));
      toast.success(choice === 'paid'
        ? 'Paid backlog on - future backlog will be AI-processed (fee confirmed before any charge)'
        : 'Free backlog on - future backlog uses smart-rule matching only');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update backlog choice');
    } finally { setSaving(null); }
  };

  const choice = info?.choice || 'free';
  const rate = info?.rate;
  const allowance = info?.free_allowance;
  const cutoff = info?.backlog_cutoff;

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid="backlog-choice-section">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid="backlog-choice-toggle"
          className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <History className="w-4 h-4 text-[#283F8B] flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">Backlog processing</p>
              <p className="text-xs text-slate-500 truncate">
                Transactions dated before {cutoff ? fmtMonth(cutoff) : 'your registration month'} · currently{' '}
                <span className="font-semibold" data-testid="backlog-choice-current">
                  {choice === 'paid' ? 'Paid (AI-supported)' : 'Free (smart-rule matching)'}
                </span>
              </p>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 p-3 rounded-lg border border-slate-200 bg-white space-y-2" data-testid="backlog-choice-panel-settings">
          <p className="text-xs text-slate-600">
            Applies to every bank account you add from now on - statement uploads and connected banks alike. Newer transactions are always processed free with full AI. Accounts already processed are not affected.
          </p>
          <button
            type="button"
            onClick={() => choose('free')}
            disabled={!!saving}
            data-testid="settings-backlog-choice-free"
            className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${choice === 'free' ? 'border-[#283F8B] bg-[#283F8B]/5' : 'border-slate-200 bg-white hover:border-slate-300'}`}
          >
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />Free backlog</span>
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                {saving === 'free' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'No fee'}
              </span>
            </div>
            <p className="text-xs text-slate-600">
              Smart-rule matching using your vendor history and our global vendor database. Anything unmatched waits in <strong>Review Needed</strong> for you to categorize. Your free allowance is not used.
            </p>
          </button>
          {!isIOSNative() && (
            <button
              type="button"
              onClick={() => choose('paid')}
              disabled={!!saving}
              data-testid="settings-backlog-choice-paid"
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${choice === 'paid' ? 'border-[#283F8B] bg-[#283F8B]/5' : 'border-slate-200 bg-white hover:border-slate-300'}`}
            >
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#283F8B]" />Paid backlog (AI-supported)</span>
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#283F8B]/10 text-[#283F8B]">
                  {saving === 'paid' ? <Loader2 className="w-3 h-3 animate-spin" /> : (rate ? `from ${rate.symbol || rate.currency}${rate.per_transaction}/tx` : 'per-tx fee')}
                </span>
              </div>
              <p className="text-xs text-slate-600">
                AI categorizes your history and creates the matching documents. Your lifetime free allowance is deducted first
                {allowance ? <> (<span data-testid="backlog-free-allowance">{allowance.remaining.toLocaleString()} of {allowance.entitlement.toLocaleString()}</span> transactions left, shared across all your bank accounts)</> : null}
                ; you confirm the exact fee before anything is charged.
              </p>
            </button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default BacklogChoiceSection;
